/**
 * Read-only HTTP probes used by the meeting health dashboard.
 *
 * These deliberately do NOT reuse the typed entity helpers in `livekit.ts`,
 * `broadcasting.ts` or `meeting.ts`: those collapse every failure mode to
 * `null`, which is exactly the information a health view needs to keep. A
 * probe reports the HTTP status, the round trip time and the parsed body so
 * the dashboard can distinguish "no broadcast for this room" (404/204) from
 * "sync is down" (0/5xx) from "sync is slow" (200, high latency).
 */
import { Entity } from '../types/interfaces';
import { ConnectedUser } from '../stores/types/connection';
import { Broadcast } from '../stores/types/broadcasting';
import { HttpRequest } from '../stores/types/types';
import { buildSyncUrl, getAuthenticatedRequestInit, getSyncHeaders } from './utils';

/** Abandon a probe after this long so one hung service cannot stall a poll. */
export const PROBE_TIMEOUT_MS = 10000;

export interface ProbeResponse<T> {
	/** HTTP status, or 0 when the request never completed (network error/timeout/abort). */
	httpStatus: number;
	/** Round trip time in ms, measured client side. */
	latencyMs: number;
	/** Parsed JSON body, or null for empty/non-JSON responses. */
	body: T | null;
	/** Populated only when the request itself failed. */
	error?: string;
}

/**
 * Room session entity value written by sync when a greenroom session starts.
 * @see greenroom-microservices/sync `initRoomSession`
 */
export interface RoomSessionValue {
	sessionId: string;
	status: 'starting' | 'started' | 'closing' | 'none';
	room: number;
	closedAt?: number;
}

/**
 * OpenTok session entity value written by sync when it provisions the session.
 * @see greenroom-microservices/sync `createOpentokSessionAndSetEntity`
 */
export interface OpentokSessionValue {
	sessionId: string;
	region?: string;
}

/**
 * GETs a sync route and records status + latency instead of throwing.
 * Never rejects: a failed request comes back as `httpStatus: 0` with `error` set.
 */
export const probeSyncEndpoint = async <T>(
	route: string,
	token: string,
): Promise<ProbeResponse<T>> => {
	const startedAt = performance.now();
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

	try {
		const response = await fetch(buildSyncUrl(route), {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
			signal: controller.signal,
		});

		// Read the body before stopping the clock so latency covers the full response.
		let body: T | null = null;
		try {
			const text = await response.text();
			body = text ? (JSON.parse(text) as T) : null;
		} catch {
			// 204s and sendStatus() responses have no JSON body - not an error here.
			body = null;
		}

		return {
			httpStatus: response.status,
			latencyMs: Math.round(performance.now() - startedAt),
			body,
		};
	} catch (error) {
		const aborted = (error as Error)?.name === 'AbortError';
		return {
			httpStatus: 0,
			latencyMs: Math.round(performance.now() - startedAt),
			body: null,
			error: aborted
				? `no response after ${PROBE_TIMEOUT_MS}ms`
				: (error as Error)?.message ?? 'request failed',
		};
	} finally {
		clearTimeout(timeout);
	}
};

/** Sync's room session entity - the top level "is this room open" signal. */
export const ProbeRoomSession = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<Entity>(`v1/room/${roomId}/session`, token);

/**
 * Sync's OpenTok session entity.
 * Note: sync will provision a session here if the room session is open but no
 * OpenTok entity exists - a self healing path, not a read only route.
 */
export const ProbeOpentokSession = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<Entity>(`opentok/${roomId}`, token);

/**
 * Sync's broadcast entity. Sync refreshes this against Spielberg on read, so a
 * 200 here also proves Spielberg answered. 204 means no broadcast entity exists.
 */
export const ProbeBroadcast = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<Entity>(`v1/room/${roomId}/broadcast`, token);

/**
 * Connected users as greenroom-socket has persisted them to sync. This is the
 * server side view of the socket channel, not this browser's user list.
 */
export const ProbeConnectedUsers = (
	token: string,
	roomId: number,
) => probeSyncEndpoint<ConnectedUser[]>(`v1/room/${roomId}/get-users`, token);

export type BroadcastEntityValue = Broadcast;

export interface ResolvedMeeting {
	/** Numeric room id every sync route expects. */
	roomId: number;
	/** The slug as it appears in producer urls. */
	slug: string;
	channel?: number;
	standaloneTitle?: string;
}

/**
 * Resolves the slug from the url to the numeric room id the sync routes need.
 *
 * `/v2/greenroom/:meeting` accepts either form - it falls back to a lookup on
 * `gr_meetings.meeting_id` when the segment is not numeric - so this also keeps
 * working if someone pastes a numeric id.
 *
 * Deliberately not `LoadMeeting()`: that helper redirects the browser to a
 * no-access view when the room is missing or deleted, which would throw a
 * producer out of the dashboard instead of telling them what happened.
 */
export const ResolveMeeting = async (
	slug: string,
	token: string,
): Promise<{ httpStatus: number; meeting: ResolvedMeeting | null; error?: string }> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v2/greenroom/${encodeURIComponent(slug)}`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (!response.ok) {
			return { httpStatus: response.status, meeting: null };
		}

		const body = await response.json();
		// A deleted room still returns 200 with status 0.
		if (!body?.meeting || body.status === 0) {
			return { httpStatus: response.status, meeting: null };
		}

		return {
			httpStatus: response.status,
			meeting: {
				roomId: Number(body.meeting),
				slug: body.meeting_id ?? slug,
				channel: body.channel,
				standaloneTitle: body.standalone_title,
			},
		};
	} catch (error) {
		return {
			httpStatus: 0,
			meeting: null,
			error: (error as Error)?.message ?? 'request failed',
		};
	}
};
